class CollectionFilters extends HTMLElement {
  constructor() {
    super();
  }

  get sectionId() {
    return this.getAttribute("data-section-id");
  }

  connectedCallback() {
    this.handleRangeInput = this.handleRangeInput.bind(this);

    this.attachEventListeners();

    // Initialize popup handlers
    this.initPopup();

    // Initialize filter action buttons
    this.initFilterActionButtons();

    // Initialize applied filters (the ones on the page)
    this.initAppliedFilters();
  }

  attachEventListeners() {
    // Remove old listeners if they exist
    this.removeEventListeners();

    // Get fresh references to DOM elements
    this.filterTabs = Array.from(this.querySelectorAll("accordion-toggle"));
    this.filterOptions = this.querySelectorAll("input");
    this.minRange = this.querySelector("input[type='range'][data-min-value]");
    this.maxRange = this.querySelector("input[type='range'][data-max-value]");
    this.minValueDisplay = this.querySelector("[data-min-value-display]");
    this.maxValueDisplay = this.querySelector("[data-max-value-display]");

    // Get all filter checkboxes (Dial Color, Case Material, etc.)
    this.filterCheckboxes = Array.from(this.querySelectorAll("input.collection-filter__checkbox-input"));

    // Get collection checkboxes separately
    this.collectionCheckboxes = Array.from(this.querySelectorAll("input.collection-filter__checkbox-collection"));

    // Store original values for reset
    if (this.minRange && this.maxRange) {
      this.originalMinValue = this.minRange.min;
      this.originalMaxValue = this.maxRange.max;
    }

    // Store original checkbox states
    this.originalCheckboxStates = new Map();
    this.filterCheckboxes.forEach(checkbox => {
      this.originalCheckboxStates.set(checkbox.id, checkbox.checked);
    });

    // Store original collection checkbox states
    this.collectionCheckboxes.forEach(checkbox => {
      this.originalCheckboxStates.set(checkbox.id, checkbox.checked);
    });

    // DON'T attach change listeners to any inputs - only manual apply
    // Only update display for range inputs
    if (this.minRange && this.maxRange) {
      this.minRange.addEventListener("input", this.handleRangeInput);
      this.maxRange.addEventListener("input", this.handleRangeInput);
    }
  }

  removeEventListeners() {
    if (this.minRange && this.maxRange) {
      this.minRange.removeEventListener("input", this.handleRangeInput);
      this.maxRange.removeEventListener("input", this.handleRangeInput);
    }
  }

  disconnectedCallback() {
    this.removeEventListeners();
  }

  initPopup() {
    const filterButton = document.querySelector('[data-filter-button]');
    const filterOverlay = document.querySelector('[data-filter-overlay]');
    const filterClose = document.querySelector('[data-filter-close]');

    const closePopup = () => {
      if (filterOverlay) {
        filterOverlay.classList.remove('is-open');
        document.body.style.overflow = '';
        document.body.style.removeProperty('overflow');
      }
    };

    const openPopup = () => {
      if (filterOverlay) {
        filterOverlay.classList.add('is-open');
        document.body.style.overflow = 'hidden';
      }
    };

    if (filterButton && filterOverlay) {
      filterButton.addEventListener('click', openPopup);
    }

    if (filterClose && filterOverlay) {
      filterClose.addEventListener('click', closePopup);
    }

    if (filterOverlay) {
      filterOverlay.addEventListener('click', (e) => {
        if (e.target === filterOverlay) {
          closePopup();
        }
      });
    }

    // Close popup with ESC key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && filterOverlay && filterOverlay.classList.contains('is-open')) {
        closePopup();
      }
    });

    // Store closePopup function for use in other methods
    this.closePopup = closePopup;
  }

  reattachSortListeners() {
    // Find the collection-sort component and re-attach its event listeners
    const sortComponent = document.querySelector('collection-sort');
    if (sortComponent) {
      const sortOptions = sortComponent.querySelectorAll('.sort-option');
      sortOptions.forEach(option => {
        // Remove old listeners by cloning
        const newOption = option.cloneNode(true);
        option.parentNode.replaceChild(newOption, option);
      });

      // Trigger re-initialization by calling connectedCallback
      if (sortComponent.connectedCallback) {
        // Store the old sortOptions reference
        const oldSortOptions = sortComponent.sortOptions;
        // Re-run connectedCallback to re-attach listeners
        sortComponent.sortOptions = sortComponent.querySelectorAll('.sort-option');
        sortComponent.sortOptions.forEach(option => {
          option.addEventListener('click', sortComponent.handleSortChange);
        });
      }
    }
  }

  initFilterActionButtons() {
    const clearAllButton = document.getElementById('clearAllFilters');
    const applyButton = document.getElementById('applyFilters');

    if (!clearAllButton || !applyButton) return;

    // Clear All Filters
    clearAllButton.addEventListener('click', () => {
      // Build clean URL without filter parameters
      const url = new URL(window.location.href);

      // Remove all filter, price, sort, and collections parameters
      const keysToDelete = [];
      url.searchParams.forEach((value, key) => {
        if (key.includes('filter.') || key.includes('price') || key.startsWith('filter[') || key === 'sort_by' || key === 'collections' || key === 'collection') {
          keysToDelete.push(key);
        }
      });

      keysToDelete.forEach(key => url.searchParams.delete(key));

      // Reset all filter checkboxes immediately in UI
      if (this.filterCheckboxes) {
        this.filterCheckboxes.forEach(checkbox => {
          checkbox.checked = false;
        });
      }

      // Reset collection checkboxes immediately in UI
      if (this.collectionCheckboxes) {
        this.collectionCheckboxes.forEach(checkbox => {
          checkbox.checked = false;
        });
      }

      // Reset price range values immediately in UI
      if (this.minRange && this.maxRange) {
        this.minRange.value = this.originalMinValue;
        this.maxRange.value = this.originalMaxValue;

        if (this.minValueDisplay) {
          this.minValueDisplay.textContent = this.formatValue(this.originalMinValue);
        }
        if (this.maxValueDisplay) {
          this.maxValueDisplay.textContent = this.formatValue(this.originalMaxValue);
        }
      }

      // Add section_id for fetching
      url.searchParams.set("section_id", this.sectionId);

      // Fetch and update without page reload
      fetch(url.toString())
        .then((response) => response.text())
        .then((html) => {
          const tempDiv = document.createElement("div");
          tempDiv.innerHTML = html;

          // Update products grid
          const currentProducts = document.querySelector(".cr-collection-grid");
          const newProducts = tempDiv.querySelector(".cr-collection-grid");

          if (currentProducts && newProducts) {
            currentProducts.innerHTML = newProducts.innerHTML;
          }

          // Update the filters content in the popup
          const currentFilters = document.querySelector(".collection-filters__content");
          const newFilters = tempDiv.querySelector(".collection-filters__content");

          if (currentFilters && newFilters) {
            currentFilters.innerHTML = newFilters.innerHTML;
          }

          // Update active filters
          const currentActiveFilters = document.querySelector(".collection-filters__active-filters");
          const newActiveFilters = tempDiv.querySelector(".collection-filters__active-filters");

          if (currentActiveFilters && newActiveFilters) {
            currentActiveFilters.innerHTML = newActiveFilters.innerHTML;
          }

          // Update applied filters (on page)
          this.updateAppliedFilters(tempDiv);

          // Update filter count badge
          const currentBadge = document.querySelector(".total-filters__applied");
          const newBadge = tempDiv.querySelector(".total-filters__applied");
          if (currentBadge && newBadge) {
            currentBadge.innerHTML = newBadge.innerHTML;
            currentBadge.style.display = newBadge.style.display;
          }

          // Update sort options to reflect reset state
          const currentSortOptions = document.querySelector(".sort-options");
          const newSortOptions = tempDiv.querySelector(".sort-options");

          if (currentSortOptions && newSortOptions) {
            currentSortOptions.innerHTML = newSortOptions.innerHTML;
          }

          // Re-attach event listeners after DOM update
          this.attachEventListeners();

          // Re-attach sort event listeners
          this.reattachSortListeners();

          // Re-attach filter action button listeners
          this.initFilterActionButtons();

          // Update product count
          const currentCount = document.querySelector(".collection-product-count");
          const newCount = tempDiv.querySelector(".collection-product-count");
          if (currentCount && newCount) {
            currentCount.textContent = newCount.textContent;
          }

          // Reinitialize load more button
          initLoadMore();

          // Update URL without section_id
          url.searchParams.delete("section_id");
          window.history.pushState({}, "", url.toString());

          // Close popup
          if (this.closePopup) {
            this.closePopup();
          }
        });
    });

    // Apply Filters
    applyButton.addEventListener('click', () => {
      // Build URL with current filter values
      const url = new URL(window.location.href);

      // Check if we're on /collections/all page
      const isAllCollectionsPage = window.location.pathname.includes('/collections/all');

      // Handle Price Range
      if (this.minRange && this.maxRange) {
        url.searchParams.delete(this.minRange.dataset.param);
        url.searchParams.delete(this.maxRange.dataset.param);

        // Only set the parameters if they differ from the original min/max values
        const currentMin = Math.round(Number(this.minRange.value));
        const currentMax = Math.round(Number(this.maxRange.value));
        const rangeMin = Math.round(Number(this.minRange.min));
        const rangeMax = Math.round(Number(this.maxRange.max));

        if (currentMin !== rangeMin || currentMax !== rangeMax) {
          url.searchParams.set(this.minRange.dataset.param, this.minRange.value);
          url.searchParams.set(this.maxRange.dataset.param, this.maxRange.value);
        }
      }

      // Handle all filter checkboxes (Dial Color, Case Material, etc.)
      if (this.filterCheckboxes) {
        // Group checkboxes by param name
        const checkboxesByParam = new Map();
        this.filterCheckboxes.forEach(checkbox => {
          const paramName = checkbox.name;
          if (!checkboxesByParam.has(paramName)) {
            checkboxesByParam.set(paramName, []);
          }
          checkboxesByParam.get(paramName).push(checkbox);
        });

        // Process each filter type
        checkboxesByParam.forEach((checkboxes, paramName) => {
          // Remove existing parameters for this filter
          const params = new URLSearchParams(url.search);
          const keysToDelete = [];
          params.forEach((value, key) => {
            if (key === paramName) {
              keysToDelete.push(key);
            }
          });
          keysToDelete.forEach(key => url.searchParams.delete(key));

          // Add checked values for this filter
          checkboxes.forEach(checkbox => {
            if (checkbox.checked) {
              url.searchParams.append(paramName, checkbox.value);
            }
          });
        });
      }

      // Handle Collection Filter (only on /collections/all page)
      let selectedCollections = [];
      if (isAllCollectionsPage && this.collectionCheckboxes) {
        // Remove existing collection parameters
        url.searchParams.delete('collections');

        // Get all checked collection handles
        selectedCollections = this.collectionCheckboxes
          .filter(checkbox => checkbox.checked)
          .map(checkbox => checkbox.dataset.collectionHandle);

        // Add selected collections as comma-separated parameter
        if (selectedCollections.length > 0) {
          url.searchParams.set('collections', selectedCollections.join(','));
        }
      }

      url.searchParams.set("section_id", this.sectionId);

      // Fetch and update without page reload
      fetch(url.toString())
        .then((response) => response.text())
        .then((html) => {
          const tempDiv = document.createElement("div");
          tempDiv.innerHTML = html;

          // If collection filter is active, fetch and merge products from selected collections
          if (isAllCollectionsPage && selectedCollections.length > 0) {
            return this.fetchCollectionProducts(selectedCollections, tempDiv, url);
          } else {
            return { tempDiv, url };
          }
        })
        .then(({ tempDiv, url }) => {
          // Update products grid
          const currentProducts = document.querySelector(".cr-collection-grid");
          const newProducts = tempDiv.querySelector(".cr-collection-grid");

          if (currentProducts && newProducts) {
            currentProducts.innerHTML = newProducts.innerHTML;
          }

          // Update the filters content in the popup
          const currentFilters = document.querySelector(".collection-filters__content");
          const newFilters = tempDiv.querySelector(".collection-filters__content");

          if (currentFilters && newFilters) {
            currentFilters.innerHTML = newFilters.innerHTML;
          }

          // Update active filters
          const currentActiveFilters = document.querySelector(".collection-filters__active-filters");
          const newActiveFilters = tempDiv.querySelector(".collection-filters__active-filters");

          if (currentActiveFilters && newActiveFilters) {
            currentActiveFilters.innerHTML = newActiveFilters.innerHTML;
          }

          // Update applied filters (on page)
          this.updateAppliedFilters(tempDiv);

          // Update filter count badge
          const currentBadge = document.querySelector(".total-filters__applied");
          const newBadge = tempDiv.querySelector(".total-filters__applied");
          if (currentBadge && newBadge) {
            currentBadge.innerHTML = newBadge.innerHTML;
            currentBadge.style.display = newBadge.style.display;
          }

          // Update sort options to preserve sort state with filters
          const currentSortOptions = document.querySelector(".sort-options");
          const newSortOptions = tempDiv.querySelector(".sort-options");

          if (currentSortOptions && newSortOptions) {
            currentSortOptions.innerHTML = newSortOptions.innerHTML;
          }

          // Re-attach event listeners after DOM update
          this.attachEventListeners();

          // Re-attach sort event listeners
          this.reattachSortListeners();

          // Restore collection checkbox states if on /collections/all
          if (isAllCollectionsPage && selectedCollections.length > 0) {
            selectedCollections.forEach(collectionHandle => {
              const checkbox = document.querySelector(`input[data-collection-handle="${collectionHandle}"]`);
              if (checkbox) {
                checkbox.checked = true;
              }
            });
            // Update collectionCheckboxes reference
            this.collectionCheckboxes = Array.from(this.querySelectorAll("input.collection-filter__checkbox-collection"));
          }

          // Re-attach filter action button listeners
          this.initFilterActionButtons();

          // Update product count
          const currentCount = document.querySelector(".collection-product-count");
          const newCount = tempDiv.querySelector(".collection-product-count");
          if (currentCount && newCount) {
            currentCount.textContent = newCount.textContent;
          }

          // Reinitialize load more button
          initLoadMore();

          // Update URL without section_id
          url.searchParams.delete("section_id");
          window.history.pushState({}, "", url.toString());

          // Close popup
          if (this.closePopup) {
            this.closePopup();
          }
        });
    });
  }

  async fetchCollectionProducts(selectedCollectionHandles, tempDiv, baseUrl) {
    // Fetch products from each selected collection
    const productsByHandle = new Map(); // Use Map to deduplicate products

    try {
      // Fetch products from each collection
      const fetchPromises = selectedCollectionHandles.map(async (collectionHandle) => {
        const collectionUrl = new URL(`/collections/${collectionHandle}`, window.location.origin);

        // Copy existing filter parameters (price, dial color, etc.)
        const params = new URLSearchParams(baseUrl.search);
        params.forEach((value, key) => {
          if (key !== 'collections' && key !== 'section_id') {
            collectionUrl.searchParams.append(key, value);
          }
        });

        collectionUrl.searchParams.set('section_id', this.sectionId);
        collectionUrl.searchParams.set('view', 'json');

        const response = await fetch(collectionUrl.toString());
        const html = await response.text();

        const collectionDiv = document.createElement('div');
        collectionDiv.innerHTML = html;

        // Extract product elements
        const products = collectionDiv.querySelectorAll('.cr-collection-product');
        products.forEach(product => {
          const productLink = product.querySelector('a[href*="/products/"]');
          if (productLink) {
            const productUrl = productLink.getAttribute('href');
            const productHandle = productUrl.split('/products/')[1]?.split('?')[0];

            if (productHandle && !productsByHandle.has(productHandle)) {
              productsByHandle.set(productHandle, product.cloneNode(true));
            }
          }
        });
      });

      await Promise.all(fetchPromises);

      // Get initial products count from settings
      const loadMoreButton = tempDiv.querySelector('#load-more-button');
      const initialCount = loadMoreButton
        ? parseInt(loadMoreButton.getAttribute('data-initial-count')) || 9
        : 9;

      const allProducts = Array.from(productsByHandle.values());
      const totalProducts = allProducts.length;

      // Update the products grid with initial products only
      const productsGrid = tempDiv.querySelector('.cr-collection-products');
      const emptyState = tempDiv.querySelector('#cr-collection-empty');

      if (productsGrid) {
        productsGrid.innerHTML = '';

        if (totalProducts > 0) {
          // Add initial products to visible grid
          const visibleProducts = allProducts.slice(0, initialCount);
          visibleProducts.forEach(product => {
            productsGrid.appendChild(product);
          });
          if (emptyState) emptyState.style.display = 'none';
        } else {
          if (emptyState) emptyState.style.display = 'block';
        }
      }

      // Update hidden products container
      const hiddenProductsContainer = tempDiv.querySelector('#cr-collection-products-hidden');
      if (hiddenProductsContainer && totalProducts > initialCount) {
        hiddenProductsContainer.innerHTML = '';
        hiddenProductsContainer.style.display = 'none';

        // Add remaining products to hidden container
        const hiddenProducts = allProducts.slice(initialCount);
        hiddenProducts.forEach(product => {
          hiddenProductsContainer.appendChild(product);
        });
      } else if (hiddenProductsContainer) {
        hiddenProductsContainer.innerHTML = '';
      }

      // Update product count
      const productCount = tempDiv.querySelector('.collection-product-count');
      if (productCount) {
        productCount.textContent = `${totalProducts} Items`;
      }

      // Update load more button visibility and data
      if (loadMoreButton) {
        if (totalProducts > initialCount) {
          loadMoreButton.style.display = 'flex';
          loadMoreButton.setAttribute('data-total-products', totalProducts);
        } else {
          loadMoreButton.style.display = 'none';
        }
      }

    } catch (error) {
      console.error('Error fetching collection products:', error);
    }

    return { tempDiv, url: baseUrl };
  }

  handleRangeInput(event) {
    // Update display in real-time while dragging
    const value = parseInt(event.currentTarget.value);
    const isMinRange = event.currentTarget.hasAttribute("data-min-value");

    if (isMinRange) {
      // Ensure min value doesn't exceed max value - 1
      const maxValue = parseInt(this.maxRange.value);
      if (value >= maxValue) {
        console.log("max value");
        event.currentTarget.value = maxValue - 10;
      }

      if (this.minValueDisplay) {
        this.minValueDisplay.textContent = this.formatValue(
          event.currentTarget.value,
          event.currentTarget
        );
      }
    } else {
      // Ensure max value doesn't go below min value + 1
      const minValue = parseInt(this.minRange.value);
      if (value <= minValue) {
        event.currentTarget.value = minValue + 10;
      }

      if (this.maxValueDisplay) {
        this.maxValueDisplay.textContent = this.formatValue(
          event.currentTarget.value,
          event.currentTarget
        );
      }
    }
  }

  updateAppliedFilters(tempDiv) {
    const currentAppliedFilters = document.querySelector(".oc-applied__filters");
    const newAppliedFilters = tempDiv.querySelector(".oc-applied__filters");

    if (currentAppliedFilters && newAppliedFilters) {
      currentAppliedFilters.innerHTML = newAppliedFilters.innerHTML;
      currentAppliedFilters.setAttribute('style', newAppliedFilters.getAttribute('style') || '');

      // If no style, and it matches hidden condition, ensure it's hidden if needed
      // Actually Liquid handle this with style="display: none;"
    }
  }

  initAppliedFilters() {
    const appliedFilters = document.querySelector('.oc-applied__filters');
    if (!appliedFilters) return;

    // Use event delegation for the remove and clear all buttons
    appliedFilters.addEventListener('click', (e) => {
      const removeBtn = e.target.closest('[data-filter-remove]');
      const clearAllBtn = e.target.closest('[data-filter-clear-all]');

      if (removeBtn || clearAllBtn) {
        e.preventDefault();
        const target = removeBtn || clearAllBtn;
        let url = new URL(target.getAttribute('href'), window.location.origin);

        // If it's a clear all button, ensure we clean the current URL params like the drawer clear all does
        if (clearAllBtn) {
          url = new URL(window.location.href);
          const keysToDelete = [];
          url.searchParams.forEach((value, key) => {
            if (key.includes('filter.') || key.includes('price') || key.startsWith('filter[') || key === 'sort_by' || key === 'collections' || key === 'collection') {
              keysToDelete.push(key);
            }
          });
          keysToDelete.forEach(key => url.searchParams.delete(key));
        }

        // Add section_id for fetching
        url.searchParams.set("section_id", this.sectionId);

        fetch(url.toString())
          .then(response => response.text())
          .then(html => {
            const tempDiv = document.createElement("div");
            tempDiv.innerHTML = html;

            // Re-use logic to update the page
            this.updatePageContent(tempDiv, url);
          });
      }
    });
  }

  updatePageContent(tempDiv, url) {
    // Update products grid
    const currentProducts = document.querySelector(".cr-collection-grid");
    const newProducts = tempDiv.querySelector(".cr-collection-grid");

    if (currentProducts && newProducts) {
      currentProducts.innerHTML = newProducts.innerHTML;
    }

    // Update the filters content in the popup
    const currentFilters = document.querySelector(".collection-filters__content");
    const newFilters = tempDiv.querySelector(".collection-filters__content");

    if (currentFilters && newFilters) {
      currentFilters.innerHTML = newFilters.innerHTML;
    }

    // Update active filters
    const currentActiveFilters = document.querySelector(".collection-filters__active-filters");
    const newActiveFilters = tempDiv.querySelector(".collection-filters__active-filters");

    if (currentActiveFilters && newActiveFilters) {
      currentActiveFilters.innerHTML = newActiveFilters.innerHTML;
    }

    // Update applied filters
    this.updateAppliedFilters(tempDiv);

    // Update filter count badge
    const currentBadge = document.querySelector(".total-filters__applied");
    const newBadge = tempDiv.querySelector(".total-filters__applied");
    if (currentBadge && newBadge) {
      currentBadge.innerHTML = newBadge.innerHTML;
      currentBadge.style.display = newBadge.style.display;
    }

    // Update sort options
    const currentSortOptions = document.querySelector(".sort-options");
    const newSortOptions = tempDiv.querySelector(".sort-options");

    if (currentSortOptions && newSortOptions) {
      currentSortOptions.innerHTML = newSortOptions.innerHTML;
    }

    // Re-attach event listeners after DOM update
    this.attachEventListeners();

    // Re-attach sort event listeners
    this.reattachSortListeners();

    // Re-attach filter action button listeners
    this.initFilterActionButtons();

    // Update product count
    const currentCount = document.querySelector(".collection-product-count");
    const newCount = tempDiv.querySelector(".collection-product-count");
    if (currentCount && newCount) {
      currentCount.textContent = newCount.textContent;
    }

    // Reinitialize load more button
    if (typeof initLoadMore === 'function') {
      initLoadMore();
    }

    // Update URL without section_id
    url.searchParams.delete("section_id");
    window.history.pushState({}, "", url.toString());
  }

  formatValue(value) {
    // Convert from cents to dollars and format as currency without decimals
    const dollars = value;
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(dollars);
  }
}

customElements.define("collection-filters", CollectionFilters);


class AccordionToggle extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.header = this.querySelector(".accordion__header");
    this.content = this.querySelector(".accordion__content");

    this.handleClick = this.handleClick.bind(this);

    this.header.addEventListener("click", this.handleClick);

    this.addMaxHeight();
  }

  disconnectedCallback() {
    this.header.removeEventListener("click", this.handleClick);
  }

  handleClick() {
    if (this.classList.contains("accordion--open")) {
      this.classList.remove("accordion--open");
    } else {
      this.classList.add("accordion--open");
    }
  }

  addMaxHeight() {
    this.style.setProperty("--accordion-content-height", this.content.scrollHeight + "px");
  }
}

customElements.define("accordion-toggle", AccordionToggle);


// Collection Sort Component
class CollectionSort extends HTMLElement {
  constructor() {
    super();
  }

  get sectionId() {
    return this.getAttribute("data-section-id");
  }

  connectedCallback() {
    this.sortOptions = this.querySelectorAll(".sort-option");
    this.handleSortChange = this.handleSortChange.bind(this);

    this.sortOptions.forEach((option) => {
      option.addEventListener("click", this.handleSortChange);
    });

    // Initialize popup handlers
    this.initPopup();

    // Check for default sort
    this.checkDefaultSort();
  }

  disconnectedCallback() {
    this.sortOptions.forEach((option) => {
      option.removeEventListener("click", this.handleSortChange);
    });
  }

  checkDefaultSort() {
    const url = new URL(window.location.href);
    const sortParam = url.searchParams.get('sort_by');

    // If sort_by is missing entirely, default to newest
    if (sortParam === null) {
      // this.updateSort('created-descending');
    }
  }

  initPopup() {
    const sortButton = document.querySelector('[data-sort-button]');
    const sortDropdown = document.querySelector('[data-sort-dropdown]');

    const closeDropdown = () => {
      if (sortDropdown) {
        sortDropdown.classList.remove('is-open');
      }
    };

    const toggleDropdown = (e) => {
      e.stopPropagation();
      if (sortDropdown) {
        sortDropdown.classList.toggle('is-open');
      }
    };

    if (sortButton && sortDropdown) {
      sortButton.addEventListener('click', toggleDropdown);
    }

    // Close dropdown when clicking outside
    document.addEventListener('click', (e) => {
      if (sortDropdown && !sortDropdown.contains(e.target) && e.target !== sortButton) {
        closeDropdown();
      }
    });

    // Close dropdown with ESC key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && sortDropdown && sortDropdown.classList.contains('is-open')) {
        closeDropdown();
      }
    });
  }

  handleSortChange(event) {
    const sortOption = event.currentTarget;
    const sortValue = sortOption.getAttribute('data-sort-value');
    this.updateSort(sortValue);
  }

  updateSort(sortValue) {
    // Build URL with sort parameter
    const url = new URL(window.location.href);

    // Explicitly set the sort value. 
    if (sortValue === '') {
      url.searchParams.delete('sort_by');
    } else {
      url.searchParams.set('sort_by', sortValue);
    }

    url.searchParams.set("section_id", this.sectionId);

    // Fetch and update products
    fetch(url.toString())
      .then((response) => response.text())
      .then((html) => {
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = html;

        // Update products grid AND load more button (wrapper)
        const currentGrid = document.querySelector(".cr-collection-grid");
        const newGrid = tempDiv.querySelector(".cr-collection-grid");

        if (currentGrid && newGrid) {
          currentGrid.innerHTML = newGrid.innerHTML;
        }

        // Update sort options to reflect new active state
        const currentSortOptions = document.querySelector(".sort-options");
        const newSortOptions = tempDiv.querySelector(".sort-options");

        if (currentSortOptions && newSortOptions) {
          currentSortOptions.innerHTML = newSortOptions.innerHTML;
        }

        // Update applied filters
        const collectionFiltersInstance = document.querySelector('collection-filters');
        if (collectionFiltersInstance && typeof collectionFiltersInstance.updateAppliedFilters === 'function') {
          collectionFiltersInstance.updateAppliedFilters(tempDiv);
        }

        // Update filter count badge
        const currentBadge = document.querySelector(".total-filters__applied");
        const newBadge = tempDiv.querySelector(".total-filters__applied");
        if (currentBadge && newBadge) {
          currentBadge.innerHTML = newBadge.innerHTML;
          currentBadge.style.display = newBadge.style.display;
        }

        // Update the sort label in the sort button
        const sortLabelMap = {
          'manual': 'Recommended',
          'price-ascending': 'Price : Low to High',
          'price-descending': 'Price : High to Low',
          'created-descending': 'Newest',
          'best-selling': 'Best Sellers'
        };
        const label = sortLabelMap[sortValue] || sortValue;
        const sortButton = document.querySelector('.sort-popup-button .selected-sort-label');
        if (sortButton) {
          sortButton.textContent = `${label}`;
        }

        // Re-attach event listeners
        this.sortOptions = this.querySelectorAll(".sort-option");
        this.sortOptions.forEach((option) => {
          option.addEventListener("click", this.handleSortChange);
        });

        // Close dropdown after sorting
        const sortDropdown = document.querySelector('[data-sort-dropdown]');
        if (sortDropdown) {
          sortDropdown.classList.remove('is-open');
        }

        // Reinitialize load more button since we replaced the grid
        if (typeof initLoadMore === 'function') {
          initLoadMore();
        }

        // Update URL without page reload
        url.searchParams.delete("section_id");
        window.history.pushState({}, "", url.toString());
      });
  }
}

customElements.define("collection-sort", CollectionSort);

// Load More Functionality with Infinite Scroll and AJAX Pagination
function initLoadMore() {
  const loadMoreButton = document.getElementById('load-more-button');

  if (!loadMoreButton) return;

  const productsGrid = document.getElementById('cr-collection-products-grid');
  let hiddenProducts = document.getElementById('cr-collection-products-hidden');

  if (!productsGrid) return;

  // Always load exactly 12 products per scroll as per requirements
  const productsPerLoad = 12;
  const initialCount = parseInt(loadMoreButton.getAttribute('data-initial-count')) || 9;
  const totalProducts = parseInt(loadMoreButton.getAttribute('data-total-products')) || 0;
  const collectionUrl = loadMoreButton.getAttribute('data-collection-url') || window.location.pathname;
  const sectionId = loadMoreButton.getAttribute('data-section-id') || '';

  // Track current page for AJAX pagination (starts at 1)
  let currentPage = 1;
  let currentlyLoaded = productsGrid.querySelectorAll('[data-product-item]').length;
  let isLoading = false; // Loading flag to prevent multiple simultaneous requests
  let allProductsLoaded = false;

  // Hide button if all products are already visible
  if (currentlyLoaded >= totalProducts) {
    loadMoreButton.style.display = 'none';
    return;
  }

  // Hide the button visually but keep it as intersection target
  const loadMoreContainer = loadMoreButton.parentElement;
  loadMoreButton.style.display = 'none';
  
  // Remove any existing infinite scroll trigger
  const existingTrigger = document.getElementById('infinite-scroll-trigger');
  if (existingTrigger) {
    existingTrigger.remove();
  }
  
  // Create infinite scroll trigger element and loading spinner
  const infiniteScrollTrigger = document.createElement('div');
  infiniteScrollTrigger.id = 'infinite-scroll-trigger';
  infiniteScrollTrigger.className = 'infinite-scroll-trigger';
  infiniteScrollTrigger.innerHTML = `
    <div class="infinite-scroll-spinner" style="display: none;">
      <div class="infinite-scroll-spinner__icon"></div>
    </div>
  `;
  loadMoreContainer.appendChild(infiniteScrollTrigger);

  const spinner = infiniteScrollTrigger.querySelector('.infinite-scroll-spinner');

  // Function to fetch more products via AJAX
  async function fetchMoreProducts() {
    currentPage++;
    
    // Build URL for next page
    const url = new URL(window.location.href);
    url.searchParams.set('page', currentPage);
    url.searchParams.set('section_id', sectionId);

    try {
      const response = await fetch(url.toString());
      const html = await response.text();
      
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;
      
      // Get products from the fetched page (both visible and hidden)
      const newVisibleProducts = tempDiv.querySelectorAll('#cr-collection-products-grid [data-product-item]');
      const newHiddenProducts = tempDiv.querySelectorAll('#cr-collection-products-hidden [data-product-item]');
      
      // Combine all products from the new page
      const allNewProducts = [...newVisibleProducts, ...newHiddenProducts];
      
      if (allNewProducts.length === 0) {
        allProductsLoaded = true;
        return [];
      }
      
      // Add new products to hidden container for processing
      if (!hiddenProducts) {
        hiddenProducts = document.createElement('div');
        hiddenProducts.id = 'cr-collection-products-hidden';
        hiddenProducts.style.display = 'none';
        productsGrid.parentElement.appendChild(hiddenProducts);
      }
      
      allNewProducts.forEach(product => {
        hiddenProducts.appendChild(product.cloneNode(true));
      });
      
      return allNewProducts;
    } catch (error) {
      console.error('Error fetching more products:', error);
      return [];
    }
  }

  // Function to load more products
  async function loadMoreProducts() {
    if (isLoading || allProductsLoaded) return;
    
    isLoading = true;
    spinner.style.display = 'flex';

    // Check if we need to fetch more products via AJAX
    let hiddenProductElements = hiddenProducts ? Array.from(hiddenProducts.querySelectorAll('[data-product-item]')) : [];
    
    // If no hidden products available, try to fetch more from server
    if (hiddenProductElements.length === 0 && currentlyLoaded < totalProducts) {
      await fetchMoreProducts();
      hiddenProductElements = hiddenProducts ? Array.from(hiddenProducts.querySelectorAll('[data-product-item]')) : [];
    }
    
    // If still no products available, we've loaded everything
    if (hiddenProductElements.length === 0) {
      spinner.style.display = 'none';
      infiniteScrollTrigger.style.display = 'none';
      isLoading = false;
      allProductsLoaded = true;
      if (observer) {
        observer.disconnect();
      }
      return;
    }

    // Small delay for better UX
    await new Promise(resolve => setTimeout(resolve, 200));

    // Calculate how many to load (exactly 12 per requirements)
    const productsToLoad = Math.min(productsPerLoad, hiddenProductElements.length);

    // Move products from hidden to visible grid
    for (let i = 0; i < productsToLoad; i++) {
      const product = hiddenProductElements[i];
      productsGrid.appendChild(product);
    }

    currentlyLoaded += productsToLoad;

    // Hide spinner
    spinner.style.display = 'none';
    isLoading = false;

    // Check if all products are loaded
    const remainingHidden = hiddenProducts ? hiddenProducts.querySelectorAll('[data-product-item]').length : 0;
    if (currentlyLoaded >= totalProducts && remainingHidden === 0) {
      infiniteScrollTrigger.style.display = 'none';
      allProductsLoaded = true;
      if (observer) {
        observer.disconnect();
      }
    }

    // Scroll animation for newly loaded products
    const newProducts = Array.from(productsGrid.children).slice(-productsToLoad);
    newProducts.forEach((product, index) => {
      setTimeout(() => {
        product.style.opacity = '0';
        product.style.transform = 'translateY(20px)';
        product.style.transition = 'opacity 0.5s ease, transform 0.5s ease';

        requestAnimationFrame(() => {
          product.style.opacity = '1';
          product.style.transform = 'translateY(0)';
        });
      }, index * 50);
    });
  }

  // Set up Intersection Observer for infinite scroll
  let observer = null;
  
  if ('IntersectionObserver' in window) {
    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !isLoading && !allProductsLoaded) {
            loadMoreProducts();
          }
        });
      },
      {
        root: null, // Use viewport as root
        rootMargin: '200px', // Start loading 200px before element comes into view
        threshold: 0.1
      }
    );

    observer.observe(infiniteScrollTrigger);
  } else {
    // Fallback for browsers that don't support Intersection Observer
    // Use scroll event with debounce
    let scrollTimeout;
    
    function handleScroll() {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        const triggerRect = infiniteScrollTrigger.getBoundingClientRect();
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
        
        // Check if trigger element is near the viewport
        if (triggerRect.top <= viewportHeight + 200 && !isLoading && !allProductsLoaded) {
          loadMoreProducts();
        }
      }, 100);
    }

    window.addEventListener('scroll', handleScroll, { passive: true });
    
    // Store reference for cleanup
    infiniteScrollTrigger._scrollHandler = handleScroll;
  }
}

// Initialize on page load
document.addEventListener('DOMContentLoaded', initLoadMore);

// Handle browser back/forward navigation
window.addEventListener('popstate', function (event) {
  // Get the current URL
  const url = new URL(window.location.href);

  // Get the section ID from the collection-filters element
  const collectionFilters = document.querySelector('collection-filters');
  if (!collectionFilters) return;

  const sectionId = collectionFilters.getAttribute('data-section-id');
  if (!sectionId) return;

  // Add section_id for fetching
  url.searchParams.set('section_id', sectionId);

  // Fetch and update the page content
  fetch(url.toString())
    .then((response) => response.text())
    .then(async (html) => {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;

      const selectedCollections = url.searchParams.get('collections')?.split(',') || [];
      if (selectedCollections.length > 0 && window.location.pathname.includes('/collections/all') && collectionFilters && typeof collectionFilters.fetchCollectionProducts === 'function') {
        return await collectionFilters.fetchCollectionProducts(selectedCollections, tempDiv, url);
      }

      return { tempDiv, url };
    })
    .then(({ tempDiv, url }) => {
      // Update products grid
      const currentProducts = document.querySelector('.cr-collection-grid');
      const newProducts = tempDiv.querySelector('.cr-collection-grid');

      if (currentProducts && newProducts) {
        currentProducts.innerHTML = newProducts.innerHTML;
      }

      // Update the filters content in the popup
      const currentFilters = document.querySelector('.collection-filters__content');
      const newFilters = tempDiv.querySelector('.collection-filters__content');

      if (currentFilters && newFilters) {
        currentFilters.innerHTML = newFilters.innerHTML;
      }

      // Update active filters
      const currentActiveFilters = document.querySelector('.collection-filters__active-filters');
      const newActiveFilters = tempDiv.querySelector('.collection-filters__active-filters');

      if (currentActiveFilters && newActiveFilters) {
        currentActiveFilters.innerHTML = newActiveFilters.innerHTML;
      }

      // Update applied filters
      if (collectionFilters && typeof collectionFilters.updateAppliedFilters === 'function') {
        collectionFilters.updateAppliedFilters(tempDiv);
      }

      // Update filter count badge
      const currentBadge = document.querySelector(".total-filters__applied");
      const newBadge = tempDiv.querySelector(".total-filters__applied");
      if (currentBadge && newBadge) {
        currentBadge.innerHTML = newBadge.innerHTML;
        currentBadge.style.display = newBadge.style.display;
      }

      // Update sort options
      const currentSortOptions = document.querySelector('.sort-options');
      const newSortOptions = tempDiv.querySelector('.sort-options');

      if (currentSortOptions && newSortOptions) {
        currentSortOptions.innerHTML = newSortOptions.innerHTML;
      }

      // Update product count
      const currentCount = document.querySelector('.collection-product-count');
      const newCount = tempDiv.querySelector('.collection-product-count');
      if (currentCount && newCount) {
        currentCount.textContent = newCount.textContent;
      }

      // Re-attach event listeners for collection-filters
      if (collectionFilters && collectionFilters.attachEventListeners) {
        collectionFilters.attachEventListeners();
      }

      // Re-attach sort event listeners
      if (collectionFilters && collectionFilters.reattachSortListeners) {
        collectionFilters.reattachSortListeners();
      }

      // Re-attach filter action button listeners
      if (collectionFilters && collectionFilters.initFilterActionButtons) {
        collectionFilters.initFilterActionButtons();
      }

      // Reinitialize load more button
      initLoadMore();
    })
    .catch((error) => {
      console.error('Error handling browser navigation:', error);
    });
});
